/**
 * 汎用モーダルコンポーネント
 *
 * 使用方法:
 *
 * HTML構造:
 * <button data-modal-trigger="modal-id">Open Modal</button>
 *
 * <div class="fac-modal" id="modal-id">
 *   <div class="fac-modal__content">
 *     <button class="close" data-modal-close="modal-id">&times;</button>
 *     <div class="fac-modal__body">
 *       <!-- モーダル内コンテンツ -->
 *     </div>
 *   </div>
 *   </div>
 *
 * 初期化:
 * new FacModal('modal-id');
 */

// YouTube IFrame Player API管理
let youtubeAPIReady = false;
let youtubeAPICallbacks = [];

window.onYouTubeIframeAPIReady = function () {
  youtubeAPIReady = true;
  youtubeAPICallbacks.forEach((cb) => cb());
  youtubeAPICallbacks = [];
};

function whenYouTubeAPIReady(callback) {
  if (youtubeAPIReady && typeof YT !== 'undefined' && typeof YT.Player !== 'undefined') {
    callback();
  } else {
    youtubeAPICallbacks.push(callback);
  }
}

class FacModal {
  constructor(modalId) {
    this.modalId = modalId;
    this.modal = document.getElementById(modalId);
    this.triggers = document.querySelectorAll(`[data-modal-trigger="${modalId}"]`);
    this.closeBtn = document.querySelector(`[data-modal-close="${modalId}"]`);
    this.iframe = this.modal?.querySelector('iframe');
    this.youtubePlayer = null;
    this.youtubePlayerReady = false;
    this.isYouTube = false;
    this.youtubeBaseSrc = '';

    if (this.modal && this.triggers.length > 0) {
      this.prepareYouTube();
      this.init();
    }
  }

  prepareYouTube() {
    if (!this.iframe) return;

    const src = this.iframe.getAttribute('data-src') || this.iframe.src || '';
    if (!src.includes('youtube.com') && !src.includes('youtu.be')) return;

    this.isYouTube = true;

    // autoplay/mute等の制御パラメータを除いたベースURLを生成
    try {
      const url = new URL(src, window.location.href);
      ['autoplay', 'mute', 'enablejsapi', 'playsinline'].forEach((p) => url.searchParams.delete(p));
      this.youtubeBaseSrc = url.toString();
    } catch (e) {
      this.youtubeBaseSrc = src;
    }

    // YT.Player紐付けに必要なID
    if (!this.iframe.id) {
      this.iframe.id = `youtube-player-${this.modalId}-${Date.now()}`;
    }

    // 【重要】クロスオリジンiframeにuser gesture (autoplay権限) を伝播させる
    // これがないと親ページのクリックがYouTube iframeに「ユーザー操作」として認識されず、
    // unMute()がブロックされる ("Unmuting failed" エラー)
    const currentAllow = this.iframe.getAttribute('allow') || '';
    const allowParts = currentAllow.split(';').map((p) => p.trim()).filter(Boolean);
    if (!allowParts.some((p) => p.startsWith('autoplay'))) {
      allowParts.push('autoplay');
    }
    this.iframe.setAttribute('allow', allowParts.join('; '));

    // 【ハック】事前ロード: ミュート状態で自動再生開始させる。
    // これによりプレイヤーが「アクティブな再生状態」になる。
    // クリック時には unMute() するだけなので、ブラウザは「再生中の音量変更」と判定して許可しやすい。
    let preloadSrc;
    try {
      const url = new URL(this.youtubeBaseSrc);
      url.searchParams.set('enablejsapi', '1');
      url.searchParams.set('playsinline', '1');
      url.searchParams.set('autoplay', '1');
      url.searchParams.set('mute', '1');
      url.searchParams.set('controls', '0');
      preloadSrc = url.toString();
    } catch (e) {
      const sep = this.youtubeBaseSrc.includes('?') ? '&' : '?';
      preloadSrc = `${this.youtubeBaseSrc}${sep}enablejsapi=1&playsinline=1&autoplay=1&mute=1&controls=0`;
    }
    this.iframe.src = preloadSrc;

    // YT.Playerをアタッチ
    whenYouTubeAPIReady(() => this.attachYouTubePlayer());
  }

  attachYouTubePlayer() {
    if (!this.iframe || typeof YT === 'undefined' || typeof YT.Player === 'undefined') return;

    try {
      this.youtubePlayer = new YT.Player(this.iframe.id, {
        events: {
          onReady: (event) => {
            this.youtubePlayerReady = true;
            try {
              event.target.setVolume(100);
            } catch (e) {}
          },
          onStateChange: (event) => {
            // ミュート自動再生が開始されたら、モーダル未表示の間は先頭で停止しておく
            if (event.data === YT.PlayerState.PLAYING && !this.modal.classList.contains('is-active')) {
              try {
                event.target.pauseVideo();
                event.target.seekTo(0, true);
              } catch (e) {}
            }
          },
        },
      });
    } catch (error) {
      console.error('Error attaching YouTube player:', error);
    }
  }

  init() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.open());
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.close();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-active')) {
        this.close();
      }
    });
  }

  // ★ハック: クリック直後の同期コンテキストで playVideo() を呼ぶことで
  // ユーザーアクティベーションを最大限活用する。
  // その後でモーダルの開閉アニメーションを実行（再生開始と表示をずらす）。
  open() {
    // 【ステップ1】クリックと同じ同期実行コンテキスト内でYouTubeを再生開始
    // これがユーザーアクティベーションを保持する鍵
    this.playYouTube();

    // 【ステップ2】HTML5動画の再生（こちらも同期コンテキスト内で）
    this.modal.querySelectorAll('video').forEach((video) => {
      video.muted = false;
      video.volume = 1.0;
      video.play().catch(() => {});
    });

    // 【ステップ3】モーダルの展開を少し遅延させる
    // 再生が確実に走り出してからモーダルを表示することで
    // 「展開＝再生」のタイミングずれによる失敗を防ぐ
    requestAnimationFrame(() => {
      this.modal.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    });
  }

  // 同期実行されることが重要なメソッド (クリックハンドラから直接呼ぶ)
  // 戦略: 既にミュートで再生開始済みのプレイヤーに対して、
  //       同期コンテキスト内で playVideo() → unMute() を実行する
  playYouTube() {
    if (!this.isYouTube) return;

    if (this.youtubePlayer && this.youtubePlayerReady) {
      try {
        this.youtubePlayer.seekTo(0, true);
        this.youtubePlayer.playVideo();
        this.youtubePlayer.unMute();
        this.youtubePlayer.setVolume(100);

        // 念のため、次のtickでもう一度unMuteを試行
        setTimeout(() => {
          try {
            if (this.youtubePlayer && this.youtubePlayer.isMuted && this.youtubePlayer.isMuted()) {
              this.youtubePlayer.unMute();
              this.youtubePlayer.setVolume(100);
            }
          } catch (e) {}
        }, 100);
      } catch (e) {
        console.error('playVideo error:', e);
      }
    } else {
      whenYouTubeAPIReady(() => {
        if (this.youtubePlayer && this.youtubePlayerReady) {
          try {
            this.youtubePlayer.seekTo(0, true);
            this.youtubePlayer.playVideo();
            this.youtubePlayer.unMute();
            this.youtubePlayer.setVolume(100);
          } catch (e) {}
        }
      });
    }
  }

  close() {
    this.modal.classList.remove('is-active');
    document.body.style.overflow = '';

    // YouTube動画の音量フェードアウト
    if (this.youtubePlayer && this.youtubePlayerReady && typeof this.youtubePlayer.getVolume === 'function') {
      this.fadeOutYouTube();
    }

    // HTML5動画の音量フェードアウト
    const videos = this.modal.querySelectorAll('video');
    videos.forEach((video) => this.fadeOutVideo(video));

    // CSSトランジション(1.2s)後に停止処理
    setTimeout(() => {
      videos.forEach((video) => {
        video.pause();
        video.currentTime = 0;
        video.volume = 1.0;
      });

      // YouTube は pause + seekTo(0) で停止（破棄せず保持 → 次回もすぐ再生可能）
      if (this.youtubePlayer && this.youtubePlayerReady) {
        try {
          this.youtubePlayer.pauseVideo();
          this.youtubePlayer.seekTo(0, true);
          this.youtubePlayer.setVolume(100);
        } catch (e) {
          console.error('Error pausing YouTube:', e);
        }
      }
    }, 1200);
  }

  fadeOutYouTube() {
    try {
      const initialVolume = this.youtubePlayer.getVolume();
      const steps = 20;
      const duration = 1200;
      const stepDuration = duration / steps;
      const decrement = initialVolume / steps;
      let currentStep = 0;

      const interval = setInterval(() => {
        currentStep++;
        const newVolume = Math.max(0, initialVolume - decrement * currentStep);
        if (this.youtubePlayer && typeof this.youtubePlayer.setVolume === 'function') {
          try {
            this.youtubePlayer.setVolume(newVolume);
          } catch (e) {}
        }
        if (currentStep >= steps || newVolume <= 0) {
          clearInterval(interval);
        }
      }, stepDuration);
    } catch (error) {
      console.error('Error fading out YouTube volume:', error);
    }
  }

  fadeOutVideo(video) {
    const initialVolume = video.volume;
    const steps = 20;
    const duration = 1200;
    const stepDuration = duration / steps;
    const decrement = initialVolume / steps;
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      const newVolume = Math.max(0, initialVolume - decrement * currentStep);
      video.volume = newVolume;
      if (currentStep >= steps || newVolume <= 0) {
        clearInterval(interval);
      }
    }, stepDuration);
  }
}

// YouTube IFrame Player APIスクリプトを読み込み
if (!window.youtubeAPILoading && !document.querySelector('script[src*="youtube.com/iframe_api"]')) {
  window.youtubeAPILoading = true;
  const tag = document.createElement('script');
  tag.src = 'https://www.youtube.com/iframe_api';
  tag.async = true;
  const firstScriptTag = document.getElementsByTagName('script')[0];
  firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
}

// 自動初期化
document.addEventListener('DOMContentLoaded', () => {
  const autoInitModals = document.querySelectorAll('[data-modal-auto-init]');
  autoInitModals.forEach((modal) => {
    new FacModal(modal.id);
  });

  // サムネイル動画の自動再生
  const thumbnailVideos = document.querySelectorAll('.content__movie .image video');
  thumbnailVideos.forEach((video) => {
    video.play().catch(() => {});
  });
});
